import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Inbox, LogOut, UserPlus, X, ShieldAlert, Newspaper, Pencil, Trash2, ExternalLink } from "lucide-react";
import { FadeUp } from "@/components/motion";
import AdminContent from "@/pages/AdminContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const loginWithGoogle = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Admin = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [enquiries, setEnquiries] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("specs");
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [webhook, setWebhook] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [posts, setPosts] = useState([]);
  const [editPost, setEditPost] = useState(null);
  const [savingPost, setSavingPost] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);

  const denied = searchParams.get("denied") === "1";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [enq, em, wh, bl] = await Promise.all([
        axios.get(`${API}/enquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/emails`, { withCredentials: true }),
        axios.get(`${API}/admin/webhook`, { withCredentials: true }),
        axios.get(`${API}/admin/blog`, { withCredentials: true }),
      ]);
      setEnquiries(enq.data);
      setEmails(em.data);
      setWebhook(wh.data);
      setPosts(bl.data);
    } catch (e) {
      toast.error("Could not load admin data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkedRef = useRef(false);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") loadData();
      return;
    }
    if (checkedRef.current) return;
    checkedRef.current = true;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch (e) {
        setUser(null);
      } finally {
        setAuthChecked(true);
      }
    };
    check();
  }, [user, loadData]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    toast.success("Signed out.");
  };

  const addEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/emails`, { email: newEmail.trim(), role: newRole }, { withCredentials: true });
      toast.success(`${newEmail.trim()} can now sign in (${newRole === "admin" ? "Admin" : "Spec library only"}).`);
      setNewEmail("");
      const { data } = await axios.get(`${API}/admin/emails`, { withCredentials: true });
      setEmails(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add that email.");
    } finally {
      setAdding(false);
    }
  };

  const changeRole = async (email, role) => {
    try {
      await axios.post(`${API}/admin/emails`, { email, role }, { withCredentials: true });
      toast.success(`${email} is now ${role === "admin" ? "Admin" : "Spec library only"}.`);
      setEmails((list) => list.map((x) => (x.email === email ? { ...x, role } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change role.");
    }
  };

  const rotateWebhook = async () => {
    setRotating(true);
    try {
      const { data } = await axios.post(`${API}/admin/webhook/rotate`, {}, { withCredentials: true });
      setWebhook(data);
      toast.success("Webhook token rotated — update it in Outrank.");
    } catch (e) {
      toast.error("Could not rotate token.");
    } finally {
      setRotating(false);
    }
  };

  const openEdit = (post) =>
    setEditPost({
      slug: post.slug,
      title: post.title || "",
      meta_description: post.meta_description || "",
      content_html: post.content_html || "",
    });

  const savePost = async (e) => {
    e.preventDefault();
    if (!editPost) return;
    setSavingPost(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/blog/${encodeURIComponent(editPost.slug)}`,
        {
          title: editPost.title,
          meta_description: editPost.meta_description,
          content_html: editPost.content_html,
        },
        { withCredentials: true }
      );
      setPosts((list) => list.map((p) => (p.slug === data.slug ? data : p)));
      setEditPost(null);
      toast.success("Article updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save article.");
    } finally {
      setSavingPost(false);
    }
  };

  const deletePost = async (slug) => {
    if (!window.confirm("Delete this article permanently? This cannot be undone.")) return;
    setDeletingSlug(slug);
    try {
      await axios.delete(`${API}/admin/blog/${encodeURIComponent(slug)}`, { withCredentials: true });
      setPosts((list) => list.filter((p) => p.slug !== slug));
      toast.success("Article deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete article.");
    } finally {
      setDeletingSlug(null);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const removeEmail = async (email) => {
    try {
      await axios.delete(`${API}/admin/emails/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success(`${email} removed.`);
      setEmails((list) => list.filter((x) => x.email !== email));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove that email.");
    }
  };

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <p className="text-sm font-medium text-ink/50">Checking access…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-24" data-testid="admin-login">
        <FadeUp className="w-full max-w-md">
          <div className="rounded-[2rem] bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-14">
            <img src="/logo-black.webp" alt="SanCub" className="mx-auto h-10 w-auto" />
            <h1 className="mt-8 font-display text-3xl font-medium tracking-tight text-ink">Admin access</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">
              Sign in with your authorised Google account to view enquiries and manage access.
            </p>
            {denied && (
              <p className="mt-6 rounded-2xl bg-red-50 px-5 py-4 text-sm text-red-700" data-testid="admin-denied-message">
                That Google account doesn't have admin access. Ask an existing admin to add it.
              </p>
            )}
            <button
              onClick={loginWithGoogle}
              data-testid="google-login-btn"
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z" />
              </svg>
              Sign in with Google
            </button>
          </div>
        </FadeUp>
      </main>
    );
  }

  if (user.role !== "admin") {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-24" data-testid="admin-specs-only">
        <FadeUp className="w-full max-w-md">
          <div className="rounded-[2rem] bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-14">
            <img src="/logo-black.webp" alt="SanCub" className="mx-auto h-10 w-auto" />
            <ShieldAlert className="mx-auto mt-8 h-8 w-8 text-ink/30" />
            <h1 className="mt-4 font-display text-3xl font-medium tracking-tight text-ink">Spec library access</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">
              {user.email} has access to the spec sheet library only — not the admin dashboard.
            </p>
            <Link
              to="/specs"
              data-testid="goto-specs-btn"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90"
            >
              Open Spec Library
            </Link>
            <button
              onClick={logout}
              data-testid="specs-only-signout"
              className="mt-4 text-[12px] font-semibold text-ink/40 underline-offset-4 transition hover:underline"
            >
              Sign out
            </button>
          </div>
        </FadeUp>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-32 md:px-10 md:pt-40" data-testid="admin-page">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <FadeUp>
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">Internal</p>
          </FadeUp>
          <h1 className="mt-4 font-display text-5xl font-medium leading-none tracking-tight text-ink md:text-6xl" data-testid="admin-headline">
            Admin
          </h1>
          <a
            href="/specs"
            data-testid="admin-spec-library-link"
            className="group mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink/50 transition-colors duration-300 hover:text-ink"
          >
            Spec sheet library →
          </a>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 rounded-full bg-white py-2 pl-2 pr-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="admin-user-chip">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sand text-xs font-bold text-ink">
                {(user.name || user.email)[0].toUpperCase()}
              </span>
            )}
            <div className="text-left">
              <p className="text-[13px] font-semibold leading-tight text-ink">{user.name || user.email}</p>
              <p className="text-[11px] leading-tight text-ink/50">{user.email}</p>
            </div>
          </div>
          <button
            onClick={loadData}
            data-testid="admin-refresh-btn"
            className="group flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-[13px] font-semibold text-white transition duration-300 hover:scale-[1.03] hover:bg-ink/90"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 rounded-full border border-ink/10 bg-white px-5 py-3 text-[13px] font-semibold text-ink transition duration-300 hover:bg-sand"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-tabs">
        {[
          { key: "enquiries", label: "Enquiries" },
          { key: "content", label: "Website content" },
          { key: "blog", label: "Blog" },
          { key: "access", label: "Access" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`rounded-full px-6 py-3 text-[13px] font-semibold transition duration-300 ${
              tab === t.key ? "bg-ink text-white" : "bg-white text-ink/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "enquiries" && (
      <>
      <p className="mt-6 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="enquiry-count">
        {loading ? "Loading…" : `${enquiries.length} ${enquiries.length === 1 ? "enquiry" : "enquiries"}`}
      </p>

      {!loading && enquiries.length === 0 && (
        <div className="mt-12 flex flex-col items-center gap-4 rounded-[2rem] bg-white py-24 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="admin-empty-state">
          <Inbox className="h-10 w-10 text-ink/30" />
          <p className="text-sm text-ink/50">No enquiries yet — submissions from the contact form will appear here.</p>
        </div>
      )}

      {enquiries.length > 0 && (
        <div className="mt-10 overflow-x-auto rounded-[2rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="enquiries-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink/5">
                {["Received", "Name", "Email", "Phone", "Interest", "Message"].map((h) => (
                  <th key={h} className="px-6 py-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/40">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enquiries.map((q) => (
                <tr key={q.id} className="border-b border-ink/5 align-top transition-colors duration-200 last:border-0 hover:bg-sand/50" data-testid={`enquiry-row-${q.id}`}>
                  <td className="whitespace-nowrap px-6 py-5 text-xs text-ink/50">
                    {new Date(q.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-6 py-5 font-semibold text-ink">{q.name}</td>
                  <td className="px-6 py-5 text-ink/70">{q.email}</td>
                  <td className="px-6 py-5 text-ink/50">{q.phone || "—"}</td>
                  <td className="px-6 py-5">
                    <span className="rounded-full bg-sand px-3.5 py-1.5 text-[11px] font-semibold text-ink/70">
                      {q.interest}
                    </span>
                  </td>
                  <td className="max-w-[320px] px-6 py-5 text-ink/60">{q.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {tab === "content" && <AdminContent />}

      {tab === "blog" && (
      <section className="mt-6" data-testid="blog-management">
        <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="blog-post-count">
          {loading ? "Loading…" : `${posts.length} ${posts.length === 1 ? "article" : "articles"}`}
        </p>

        {!loading && posts.length === 0 && (
          <div className="mt-12 flex flex-col items-center gap-4 rounded-[2rem] bg-white py-24 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="blog-empty-state">
            <Newspaper className="h-10 w-10 text-ink/30" />
            <p className="max-w-sm text-sm text-ink/50">
              No articles yet. Connect Outrank with the webhook details on the Access tab and published articles will appear here.
            </p>
          </div>
        )}

        {posts.length > 0 && (
          <div className="mt-8 space-y-4" data-testid="blog-post-list">
            {posts.map((p) => (
              <div
                key={p.slug}
                className="flex flex-col gap-4 rounded-[1.5rem] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:flex-row sm:items-center sm:p-6"
                data-testid={`blog-post-row-${p.slug}`}
              >
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="h-16 w-24 shrink-0 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl bg-sand">
                    <Newspaper className="h-5 w-5 text-ink/30" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{p.title}</p>
                  <p className="mt-0.5 text-xs text-ink/40">
                    /blog/{p.slug}
                    {p.published_at && (
                      <> · {new Date(p.published_at).toLocaleDateString("en-AU", { dateStyle: "medium" })}</>
                    )}
                  </p>
                  {p.meta_description && (
                    <p className="mt-1 line-clamp-1 text-xs text-ink/50">{p.meta_description}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`blog-view-${p.slug}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 text-ink/50 transition duration-300 hover:bg-sand hover:text-ink"
                    aria-label={`View ${p.title}`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`blog-edit-${p.slug}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 text-ink/50 transition duration-300 hover:bg-sand hover:text-ink"
                    aria-label={`Edit ${p.title}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deletePost(p.slug)}
                    disabled={deletingSlug === p.slug}
                    data-testid={`blog-delete-${p.slug}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 text-ink/50 transition duration-300 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label={`Delete ${p.title}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {editPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm" data-testid="blog-edit-modal" onClick={() => setEditPost(null)}>
          <form
            onSubmit={savePost}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-5 overflow-y-auto rounded-[2rem] bg-white p-8 shadow-2xl md:p-10"
            data-testid="blog-edit-form"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-3xl font-medium tracking-tight text-ink">Edit article</h2>
                <p className="mt-1 text-xs text-ink/40">/blog/{editPost.slug}</p>
              </div>
              <button type="button" onClick={() => setEditPost(null)} data-testid="blog-edit-close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/10 text-ink/50 transition hover:bg-sand" aria-label="Close editor">
                <X className="h-4 w-4" />
              </button>
            </div>
            <label className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink/40">Title</span>
              <input
                type="text"
                required
                value={editPost.title}
                onChange={(e) => setEditPost({ ...editPost, title: e.target.value })}
                data-testid="blog-edit-title"
                className="rounded-2xl border border-ink/10 bg-sand/60 px-5 py-3.5 text-sm text-ink outline-none transition-colors duration-300 focus:border-ink/40 focus:bg-white"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink/40">Meta description (SEO)</span>
              <textarea
                rows={2}
                value={editPost.meta_description}
                onChange={(e) => setEditPost({ ...editPost, meta_description: e.target.value })}
                data-testid="blog-edit-meta"
                className="resize-none rounded-2xl border border-ink/10 bg-sand/60 px-5 py-3.5 text-sm text-ink outline-none transition-colors duration-300 focus:border-ink/40 focus:bg-white"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink/40">Content (HTML)</span>
              <textarea
                rows={12}
                value={editPost.content_html}
                onChange={(e) => setEditPost({ ...editPost, content_html: e.target.value })}
                data-testid="blog-edit-content"
                className="resize-y rounded-2xl border border-ink/10 bg-sand/60 px-5 py-3.5 font-mono text-[13px] leading-relaxed text-ink outline-none transition-colors duration-300 focus:border-ink/40 focus:bg-white"
              />
            </label>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditPost(null)}
                data-testid="blog-edit-cancel"
                className="rounded-full border border-ink/10 bg-white px-6 py-3 text-[13px] font-semibold text-ink transition duration-300 hover:bg-sand"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingPost}
                data-testid="blog-edit-save"
                className="rounded-full bg-ink px-7 py-3 text-[13px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90 disabled:opacity-60"
              >
                {savingPost ? "Saving…" : "Save article"}
              </button>
            </div>
          </form>
        </div>
      )}

      {tab === "access" && (
      <section className="mt-16" data-testid="access-management">
        <FadeUp>
          <h2 className="font-display text-3xl font-medium tracking-tight text-ink md:text-4xl">Manage access</h2>
          <p className="mt-2 max-w-lg text-sm leading-relaxed text-ink/60">
            Only these Google accounts can sign in. Admins get this dashboard (enquiries, website
            content, access); Specs only accounts get just the spec sheet library at /specs.
          </p>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-8">
          <div className="rounded-[2rem] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10">
            <form onSubmit={addEmail} className="flex flex-col gap-3 sm:flex-row" data-testid="add-email-form">
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="teammate@tilesmate.com.au"
                data-testid="add-email-input"
                className="flex-1 rounded-full border border-ink/10 bg-sand/60 px-6 py-3.5 text-sm text-ink placeholder:text-ink/40 outline-none transition-colors duration-300 focus:border-ink/40 focus:bg-white"
              />
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                data-testid="add-email-role"
                className="rounded-full border border-ink/10 bg-sand/60 px-5 py-3.5 text-sm font-semibold text-ink outline-none transition-colors duration-300 focus:border-ink/40"
              >
                <option value="specs">Specs only</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                disabled={adding}
                data-testid="add-email-btn"
                className="flex items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[13px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90 disabled:opacity-60"
              >
                <UserPlus className="h-4 w-4" />
                Add access
              </button>
            </form>
            <ul className="mt-8 divide-y divide-ink/5" data-testid="admin-email-list">
              {emails.map((e) => (
                <li key={e.email} className="flex items-center justify-between gap-4 py-4" data-testid={`admin-email-${e.email}`}>
                  <div>
                    <p className="text-sm font-semibold text-ink">{e.email}</p>
                    <p className="mt-0.5 text-xs text-ink/40">
                      {e.email === "shivam@tilesmate.com.au" ? "Owner" : e.added_by === "system" ? "Default access" : `Added by ${e.added_by}`}
                      {e.email === user.email && " · you"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {e.email === user.email ? (
                      <span className="rounded-full bg-ink px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-white" data-testid={`role-badge-${e.email}`}>
                        Admin
                      </span>
                    ) : (
                      <select
                        value={e.role || "admin"}
                        onChange={(ev) => changeRole(e.email, ev.target.value)}
                        data-testid={`role-select-${e.email}`}
                        className="rounded-full border border-ink/10 bg-sand/60 px-4 py-2 text-[12px] font-semibold text-ink outline-none"
                      >
                        <option value="admin">Admin</option>
                        <option value="specs">Specs only</option>
                      </select>
                    )}
                    {e.email !== user.email && (
                      <button
                        onClick={() => removeEmail(e.email)}
                        data-testid={`remove-email-${e.email}`}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 text-ink/50 transition duration-300 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                        aria-label={`Remove ${e.email}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-6 flex items-start gap-2 rounded-2xl bg-sand/70 px-5 py-4 text-xs leading-relaxed text-ink/50">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              Removing an email revokes access immediately — even if that person is currently signed in.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-6">
          <div className="rounded-[2rem] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-12" data-testid="webhook-card">
            <h2 className="font-display text-3xl font-medium tracking-tight text-ink">Blog webhook</h2>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-ink/60">
              Paste these into your Outrank dashboard (webhook settings) to auto-publish articles
              straight to the Journal at /blog.
            </p>
            {webhook && (
              <div className="mt-6 space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <span className="w-24 shrink-0 text-[11px] font-bold uppercase tracking-[0.12em] text-ink/40">Endpoint</span>
                  <code className="flex-1 truncate rounded-full bg-sand/70 px-5 py-3 text-[13px] text-ink" data-testid="webhook-endpoint">
                    {`${process.env.REACT_APP_BACKEND_URL}${webhook.endpoint}`}
                  </code>
                  <button
                    onClick={() => copyText(`${process.env.REACT_APP_BACKEND_URL}${webhook.endpoint}`, "Endpoint")}
                    data-testid="webhook-copy-endpoint"
                    className="shrink-0 rounded-full border border-ink/10 bg-white px-5 py-2.5 text-[12px] font-semibold text-ink transition duration-300 hover:bg-sand"
                  >
                    Copy
                  </button>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <span className="w-24 shrink-0 text-[11px] font-bold uppercase tracking-[0.12em] text-ink/40">Token</span>
                  <code className="flex-1 truncate rounded-full bg-sand/70 px-5 py-3 text-[13px] text-ink" data-testid="webhook-token">
                    {webhook.token}
                  </code>
                  <div className="flex shrink-0 gap-2">
                    <button
                      onClick={() => copyText(webhook.token, "Token")}
                      data-testid="webhook-copy-token"
                      className="rounded-full border border-ink/10 bg-white px-5 py-2.5 text-[12px] font-semibold text-ink transition duration-300 hover:bg-sand"
                    >
                      Copy
                    </button>
                    <button
                      onClick={rotateWebhook}
                      disabled={rotating}
                      data-testid="webhook-rotate-btn"
                      className="rounded-full bg-ink px-5 py-2.5 text-[12px] font-semibold text-white transition duration-300 hover:bg-ink/90 disabled:opacity-60"
                    >
                      {rotating ? "Rotating…" : "Rotate token"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </FadeUp>
      </section>
      )}
    </main>
  );
};

export default Admin;
